import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import { Map, List } from 'immutable';
import { useContextMenu } from '../contexts/ContextMenuContext';
import { normalizeForSearch } from '../utils/searchUtils';
import { copyToClipboard } from '../utils/copyToClipboard';
import StateNode from './StateNode';
import PrimitiveValue from './PrimitiveValue';

interface ImmutableRendererProps {
    data: any;
    searchPath?: string | null;
    currentPath?: string;
    expandedPaths?: Record<string, boolean>;
    matchingPaths?: string[];
    onHoverPath?: (path: string) => void;
}

/**
 * Component for recursively rendering Immutable.js objects
 * @param props - Component props
 * @returns Rendered component
 */
const ImmutableRenderer = memo(
    ({
        data,
        searchPath = '',
        currentPath = '',
        expandedPaths = {},
        matchingPaths = [],
        onHoverPath,
    }: ImmutableRendererProps) => {
        const [internalExpandedKeys, setInternalExpandedKeys] = useState<
            Record<string, boolean>
        >({});
        const { openContextMenu } = useContextMenu();

        // Combine external path expansion (from search) with internal expand/collapse state
        const expandedKeys = useMemo(() => {
            return { ...internalExpandedKeys, ...expandedPaths };
        }, [internalExpandedKeys, expandedPaths]);

        const toggleExpand = useCallback(
            (key: string, path: string | null = null) => {
                // For array items and list items, we need to use the full path as the key
                const toggleKey = path ? `${path}.${key}` : key;
                const uniqueToggleKey = !isNaN(Number(key))
                    ? `array-item-${toggleKey}` // Use array-item prefix for numeric keys (array indices)
                    : toggleKey;

                setInternalExpandedKeys((prev) => ({
                    ...prev,
                    [uniqueToggleKey]: !prev[uniqueToggleKey],
                }));
            },
            [],
        );

        // Fixed collapse function - ensures parent node remains collapsible afterward
        const collapseChildren = useCallback((path: string) => {
            if (!path) return;

            console.log(`Collapsing ALL descendants of node: "${path}"`);

            setInternalExpandedKeys((prev) => {
                // Start with a fresh state object
                const newState: Record<string, boolean> = {};

                // Step 1: Instead of forcing the parent node to stay expanded,
                // just preserve its current expansion state (if any)
                if (prev[path] !== undefined) {
                    newState[path] = prev[path];
                }

                // Preserve alternate formats of the parent path with their original values
                // This allows them to be properly toggled later
                const parentFormats = [
                    `array-item-${path}`,
                    `list-item-${path}`,
                    `path-${path}`,
                ];
                parentFormats.forEach((format) => {
                    if (prev[format] !== undefined) {
                        newState[format] = prev[format];
                    }
                });

                // Step 2: Copy keys unrelated to our path
                Object.keys(prev).forEach((key) => {
                    // Skip keys we've already processed
                    if (newState[key] !== undefined) return;

                    const keyLower = key.toLowerCase();
                    const pathLower = path.toLowerCase();

                    // Skip children of our path
                    if (
                        keyLower === pathLower ||
                        keyLower.startsWith(`${pathLower}.`)
                    )
                        return;

                    // Skip prefixed versions of our path's children
                    for (const prefix of [
                        'array-item-',
                        'list-item-',
                        'path-',
                    ]) {
                        if (keyLower.startsWith(`${prefix}${pathLower}`))
                            return;
                    }

                    // Skip exact-array keys containing our path
                    if (key.startsWith('exact-array-') && key.includes(path))
                        return;

                    // Special handling for peer nodes
                    if (path.includes('.')) {
                        const lastDot = path.lastIndexOf('.');
                        const parentPath = path.substring(0, lastDot);
                        const childKey = path.substring(lastDot + 1);

                        // Skip paths that are peer nodes of our target
                        if (
                            keyLower.startsWith(parentPath.toLowerCase()) &&
                            key !== parentPath &&
                            !keyLower.startsWith(
                                `${parentPath.toLowerCase()}.${childKey.toLowerCase()}`,
                            )
                        ) {
                            return;
                        }
                    }

                    // Keep all other keys
                    newState[key] = prev[key];
                });

                return newState;
            });
        }, []);

        // Add debug info for expanded keys when needed
        useEffect(() => {
            console.debug(
                `Current expanded keys count: ${Object.keys(internalExpandedKeys).length}`,
            );
        }, [internalExpandedKeys]);

        const handleContextMenu = useCallback(
            (e: React.MouseEvent, item: any, path: string) => {
                e.preventDefault();
                e.stopPropagation();

                // Extract the last segment from the path for the key
                const segments = path.split('.');
                const key = segments[segments.length - 1];

                const menuItems = [
                    {
                        label: 'Copy Value',
                        action: () => copyToClipboard(item),
                    },
                    {
                        label: 'Copy with Index',
                        action: () => copyToClipboard(item, key),
                    },
                    {
                        label: 'Copy Full Path',
                        action: () => copyToClipboard(item, null, path),
                    },
                ];

                // Add collapse children option if the item is an object or array
                if (
                    typeof item === 'object' &&
                    item !== null &&
                    (Object.keys(item).length > 0 ||
                        (Array.isArray(item) && item.length > 0) ||
                        (Map.isMap(item) && item.size > 0) ||
                        (List.isList(item) && item.size > 0))
                ) {
                    menuItems.push({
                        label: 'Collapse Children',
                        action: () => collapseChildren(path),
                    });
                }

                openContextMenu({
                    x: e.clientX,
                    y: e.clientY,
                    menuItems,
                });
            },
            [openContextMenu, collapseChildren],
        );

        // New handler for mouse enter on any node
        const handleNodeMouseEnter = useCallback(
            (path: string) => {
                // Pass the full path up to parent components
                if (onHoverPath) onHoverPath(path);
            },
            [onHoverPath],
        );

        // New handler for mouse leave - use empty string to avoid null issues
        const handleNodeMouseLeave = useCallback(() => {
            // Clear path when mouse leaves - explicitly set to empty string
            if (onHoverPath) onHoverPath('');
        }, [onHoverPath]);

        const renderValue = useCallback(
            (
                value: any,
                nestLevel = 0,
                path = currentPath,
            ): React.ReactNode => {
                // Limit rendering depth to prevent deep recursion
                if (nestLevel > 8) {
                    return (
                        <span className="text-gray-500">
                            Max depth reached...
                        </span>
                    );
                }

                // Handle immutable maps
                if (Map.isMap(value)) {
                    const entries = value
                        .entrySeq()
                        .sortBy(([k]) => k)
                        .toArray();

                    return (
                        <div style={{ marginLeft: `${nestLevel * 10}px` }}>
                            {entries.map(([k, v]) => {
                                const keyPath = path ? `${path}.${k}` : k;
                                // Check if this path is on the way to a matching value
                                const isOnPathToMatch = matchingPaths.some(
                                    (matchPath) => {
                                        return (
                                            matchPath.startsWith(
                                                keyPath + '.',
                                            ) ||
                                            (path &&
                                                matchPath.startsWith(
                                                    path + '.',
                                                ) &&
                                                matchPath.includes(`.${k}.`))
                                        );
                                    },
                                );

                                // Determine if this item is expanded
                                const isExpanded =
                                    expandedKeys[k] ||
                                    expandedKeys[keyPath] ||
                                    (searchPath &&
                                        normalizeForSearch(keyPath) ===
                                            normalizeForSearch(searchPath)) ||
                                    isOnPathToMatch;

                                return (
                                    <StateNode
                                        key={k}
                                        nodeKey={k}
                                        nodeValue={v}
                                        path={path}
                                        searchPath={searchPath}
                                        matchingPaths={matchingPaths}
                                        nestLevel={nestLevel}
                                        isExpanded={isExpanded}
                                        onToggle={toggleExpand}
                                        renderValue={renderValue}
                                        onCollapseChildren={collapseChildren}
                                        onMouseEnter={() =>
                                            handleNodeMouseEnter(keyPath)
                                        }
                                        onMouseLeave={handleNodeMouseLeave}
                                    />
                                );
                            })}
                        </div>
                    );
                }

                // Handle immutable lists
                if (List.isList(value)) {
                    const list = value.toArray();

                    return (
                        <div style={{ marginLeft: `${nestLevel * 10}px` }}>
                            {list.map((item, index) => {
                                const indexPath = path
                                    ? `${path}.${index}`
                                    : `${index}`;

                                // Check if this array item's path is on the way to a matching value
                                const isOnPathToMatch = matchingPaths.some(
                                    (matchPath) => {
                                        return (
                                            matchPath.startsWith(
                                                indexPath + '.',
                                            ) ||
                                            (path &&
                                                matchPath.startsWith(
                                                    path + '.',
                                                ) &&
                                                matchPath.includes(
                                                    `.${index}.`,
                                                ))
                                        );
                                    },
                                );

                                // Use full path as toggle key including prefix to avoid collisions
                                const itemToggleKey = `list-item-${indexPath}`;
                                const exactItemKey = `exact-array-${path}-${index}`;

                                // Check multiple key formats for expansion state
                                const isItemExpanded =
                                    expandedKeys[itemToggleKey] ||
                                    expandedKeys[exactItemKey] ||
                                    expandedKeys[`path-${indexPath}`] ||
                                    expandedKeys[`array-item-${indexPath}`] ||
                                    isOnPathToMatch;

                                // Check if item is an object or array that can be expanded
                                const isExpandableItem =
                                    typeof item === 'object' &&
                                    item !== null &&
                                    (Object.keys(item).length > 0 ||
                                        (Array.isArray(item) &&
                                            item.length > 0) ||
                                        (Map.isMap(item) && item.size > 0) ||
                                        (List.isList(item) && item.size > 0));

                                // For list items, render our own UI instead of using StateNode
                                return (
                                    <div
                                        key={index}
                                        className="my-1"
                                        onContextMenu={(e) =>
                                            handleContextMenu(
                                                e,
                                                item,
                                                indexPath,
                                            )
                                        }
                                        onMouseEnter={() =>
                                            handleNodeMouseEnter(indexPath)
                                        }
                                        onMouseLeave={handleNodeMouseLeave}
                                    >
                                        {isExpandableItem ? (
                                            <div className="flex items-start justify-between">
                                                <div
                                                    onClick={() =>
                                                        toggleExpand(
                                                            String(index),
                                                            path,
                                                        )
                                                    }
                                                    className="ml-4 flex cursor-pointer font-semibold hover:text-gray-600"
                                                >
                                                    <div className="w-6">
                                                        {isItemExpanded
                                                            ? '📂'
                                                            : '📁'}
                                                    </div>
                                                    [{index}]:
                                                </div>
                                                <div className="flex items-center">
                                                    {!isItemExpanded && (
                                                        <div className="ml-2 text-gray-500">
                                                            {Array.isArray(item)
                                                                ? `Array(${item.length})`
                                                                : Map.isMap(
                                                                        item,
                                                                    )
                                                                  ? `Map(${item.size})`
                                                                  : List.isList(
                                                                          item,
                                                                      )
                                                                    ? `List(${item.size})`
                                                                    : `Object(${
                                                                          Object.keys(
                                                                              item,
                                                                          )
                                                                              .length
                                                                      })`}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <span className="mr-2 ml-10 font-semibold">
                                                        [{index}]:
                                                    </span>
                                                    <PrimitiveValue
                                                        value={item}
                                                        searchPath={searchPath}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {isExpandableItem && isItemExpanded && (
                                            <div className="pl-4">
                                                {renderValue(
                                                    item,
                                                    nestLevel + 1,
                                                    indexPath,
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                }

                // Handle arrays
                if (Array.isArray(value)) {
                    return (
                        <div style={{ marginLeft: `${nestLevel * 10}px` }}>
                            {value.map((item, index) => {
                                const indexPath = path
                                    ? `${path}.${index}`
                                    : `${index}`;

                                // Check if this array item's path is on the way to a matching value
                                const isOnPathToMatch = matchingPaths.some(
                                    (matchPath) => {
                                        return (
                                            matchPath.startsWith(
                                                indexPath + '.',
                                            ) ||
                                            (path &&
                                                matchPath.startsWith(
                                                    path + '.',
                                                ) &&
                                                matchPath.includes(
                                                    `.${index}.`,
                                                ))
                                        );
                                    },
                                );

                                // Use full path as toggle key including prefix to avoid collisions
                                const itemToggleKey = `array-item-${indexPath}`;
                                const exactItemKey = `exact-array-${path}-${index}`;

                                // Check multiple key formats for expansion state
                                const isItemExpanded =
                                    expandedKeys[itemToggleKey] ||
                                    expandedKeys[exactItemKey] ||
                                    expandedKeys[`path-${indexPath}`] ||
                                    isOnPathToMatch;

                                // Check if item is an object or array that can be expanded
                                const isExpandableItem =
                                    typeof item === 'object' &&
                                    item !== null &&
                                    (Object.keys(item).length > 0 ||
                                        (Array.isArray(item) &&
                                            item.length > 0) ||
                                        (Map.isMap(item) && item.size > 0) ||
                                        (List.isList(item) && item.size > 0));

                                // For array items, render our own UI instead of using StateNode
                                return (
                                    <div
                                        key={index}
                                        className="my-1"
                                        onContextMenu={(e) =>
                                            handleContextMenu(
                                                e,
                                                item,
                                                indexPath,
                                            )
                                        }
                                        onMouseEnter={() =>
                                            handleNodeMouseEnter(indexPath)
                                        }
                                        onMouseLeave={handleNodeMouseLeave}
                                    >
                                        {isExpandableItem ? (
                                            <div className="flex items-start justify-between">
                                                <div
                                                    onClick={() =>
                                                        toggleExpand(
                                                            String(index),
                                                            path,
                                                        )
                                                    }
                                                    className="ml-4 flex cursor-pointer font-semibold hover:text-gray-600"
                                                >
                                                    <div className="w-6">
                                                        {isItemExpanded
                                                            ? '📂'
                                                            : '📁'}
                                                    </div>
                                                    [{index}]:
                                                </div>
                                                <div className="flex items-center">
                                                    {!isItemExpanded && (
                                                        <div className="ml-2 text-gray-500">
                                                            {Array.isArray(item)
                                                                ? `Array(${item.length})`
                                                                : Map.isMap(
                                                                        item,
                                                                    )
                                                                  ? `Map(${item.size})`
                                                                  : List.isList(
                                                                          item,
                                                                      )
                                                                    ? `List(${item.size})`
                                                                    : `Object(${
                                                                          Object.keys(
                                                                              item,
                                                                          )
                                                                              .length
                                                                      })`}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <span className="mr-2 ml-10 font-semibold">
                                                        [{index}]:
                                                    </span>
                                                    <PrimitiveValue
                                                        value={item}
                                                        searchPath={searchPath}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {isExpandableItem && isItemExpanded && (
                                            <div className="pl-4">
                                                {renderValue(
                                                    item,
                                                    nestLevel + 1,
                                                    indexPath,
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                }

                // Primitive values - use memoized component
                return <PrimitiveValue value={value} searchPath={searchPath} />;
            },
            [
                currentPath,
                expandedKeys,
                matchingPaths,
                searchPath,
                toggleExpand,
                collapseChildren,
                handleContextMenu,
                handleNodeMouseEnter,
                handleNodeMouseLeave,
            ],
        );

        return renderValue(data);
    },
);

export default ImmutableRenderer;
